import { createOrder, setCreateOrderStatus } from "actions/balance";
import SageButton from "kit/SageButton";
import SageErrorAlert from "kit/SageErrorAlert";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import createOrderStatusSelector from "selectors/createOrderStatus";
import { centHundredthsToString } from "utils/moneyFormat";

const BalanceMinutesOrderForm: React.FC = () => {
    const dispatch = useDispatch();
    const [minutes, setMinutes] = React.useState(10);
    const [purchasePrice, setPurchasePrice] = React.useState(100);

    React.useEffect(() => {
        dispatch(setCreateOrderStatus("none"));
    }, [dispatch]);

    const status = useSelector(createOrderStatusSelector);

    const onFormSubmit = React.useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            dispatch(createOrder(minutes, purchasePrice));
        },
        [dispatch, minutes, purchasePrice]
    );

    const onErrorDismiss = React.useCallback(() => {
        dispatch(setCreateOrderStatus("none"));
    }, [dispatch]);

    const onSetMinutes = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setMinutes(parseInt(e.target.value.replace(/\D/g, "")));
        },
        [setMinutes]
    );

    const onSetPurchasePrice = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setPurchasePrice(parseInt(e.target.value.replace(/\D/g, "")));
        },
        [setPurchasePrice]
    );

    const errorText = React.useMemo(() => {
        if (status === "error") {
            return "An unknown error occurred when creating your order.";
        } else if (status === "insufficient_funds") {
            return "You do not have enough money in your account to purchase this many minutes";
        } else if (status === "request_parse_error") {
            return "Invalid input. Make sure your price and minutes are both valid.";
        } else if (status === "conflict") {
            return "You have an open sale. Please cancel it before creating your order.";
        } else {
            return null;
        }
    }, [status]);

    return (
        <>
            <form onSubmit={onFormSubmit}>
                <div className="mb-3">
                    <label htmlFor="minutes" className="form-label">
                        Minutes to Buy
                    </label>
                    <input
                        value={isNaN(minutes) ? "" : minutes}
                        onChange={onSetMinutes}
                        type="number"
                        id="minutes"
                        className="form-control"
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="price">
                        Purchase Price per Minute in 1/100th of cents
                    </label>
                    <input
                        value={purchasePrice}
                        onChange={onSetPurchasePrice}
                        type="number"
                        id="price"
                        className="form-control"
                    ></input>
                </div>
                <p>
                    Total Purchase Price:{" "}
                    {centHundredthsToString(
                        isNaN(minutes * purchasePrice)
                            ? 0
                            : minutes * purchasePrice
                    )}
                </p>
                <SageButton
                    loading={status === "loading"}
                    text="Purchase"
                    type="submit"
                />
            </form>
            {errorText && (
                <SageErrorAlert
                    text={errorText || undefined}
                    onDismiss={onErrorDismiss}
                />
            )}
        </>
    );
};

export default BalanceMinutesOrderForm;
